import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@cloud-ui/contexts/ToastContext';
import { createApiToken, listApiTokens, revokeApiToken } from '@cloud-ui/utils/api/tokens';
import type { CreateApiTokenRequestDTO } from '@shared/dto/apiTokens';

interface UseCreateApiTokenOptions {
  showToasts?: boolean;
}

export const useApiTokens = () => {
  return useQuery({
    queryKey: ['apiTokens'],
    queryFn: listApiTokens,
  });
};

export const useCreateApiToken = ({ showToasts = true }: UseCreateApiTokenOptions = {}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: CreateApiTokenRequestDTO) => createApiToken(data),
    onSuccess: () => {
      if (showToasts) {
        showToast('API token created successfully', 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['apiTokens'] });
    },
    onError: (error) => {
      console.error('Failed to create API token:', error);
      if (showToasts) {
        showToast('Failed to create API token', 'error');
      }
    },
  });
};

export const useRevokeApiToken = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (tokenId: string) => revokeApiToken(tokenId),
    onSuccess: () => {
      showToast('API token revoked successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['apiTokens'] });
    },
    onError: (error) => {
      console.error('Failed to revoke API token:', error);
      showToast('Failed to revoke API token', 'error');
    },
  });
};
